import React from 'react';
import { Box, Typography, Card, CardContent, Chip, Grid } from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { Video, Mic } from 'lucide-react';
import { ICompanyDetails } from '../../../redux/company/types';
import { isEmpty } from '../../../utils/lodash';
import { formatCurrencyWithAbbrevation } from '../../../constants/currency';
import { convertMinuteSecondsDays } from '../../../utils/AppUtils';
import CustomButton from '../../Button/CustomButton';
import Pagination from '../../Common/Pagination';
import { useDispatch } from 'react-redux';
import { getCompanyDetails } from '../../../redux/company/actions';
import { useParams } from 'react-router-dom';
import { IRole } from '../../../types/global/helper';
import { replaceRouteParam } from '../../../utils/routesProcessor';
import { navigate } from '../../../redux/navigator/action';
import { ROUTES } from '../../../types/global/routes.types';
import { RichTextViewer } from '../../Common/RichTextViewer';

interface IAuditionsTab {
    companyDetails?: ICompanyDetails;
}

const AuditionsTab: React.FC<IAuditionsTab> = ({ companyDetails }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const params = useParams();
    const { id } = params;
    const { data, pagination } = companyDetails?.auditions! || {};

    const handlePageChange = (page: number) => {
        if (id) {
            dispatch(
                getCompanyDetails({
                    slug: id,
                    auditionsPage: page,
                    auditionsLimit: pagination?.pageSize || 10,
                    withAuditions: true,
                    singleFetch: true,
                }),
            );
        }
    };

    const getAuditionIcon = (auditionType: string) => {
        if (!auditionType) return Video;
        switch (auditionType) {
            case 'voice over':
            case 'voiceover':
                return Mic;
            case 'video':
            case 'commercial':
            case 'film':
            default:
                return Video;
        }
    };

    const getAuditionIconColor = (auditionType: string) => {
        if (!auditionType) return '#10B981';
        switch (auditionType) {
            case 'voice over':
            case 'voiceover':
                return '#EC4899';
            case 'commercial':
                return '#EF4444';
            case 'film':
                return '#3B82F6';
            default:
                return '#10B981';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'casting now':
            case 'open':
                return '#10B981';
            case 'closed':
                return '#EF4444';
            case 'pending':
                return '#F59E0B';
            default:
                return '#10B981';
        }
    };

    const handleViewDetails = (role: IRole) => {
        const route = replaceRouteParam(ROUTES.AUDITION_DETAIL, 'auditionId', role.audition.slug!);
        dispatch(
            navigate({
                routes: route,
            }),
        );
    };

    if (!data || isEmpty(data)) {
        return (
            <Box
                sx={{
                    textAlign: 'center',
                    py: 4,
                    color: theme[themeStyle].tag_white,
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                        fontSize: '1rem',
                    }}>
                    No auditions available at the moment.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 4,
                }}>
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                        }}>
                        Open Auditions ({pagination?.totalCount || data.length})
                    </Typography>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                            fontSize: '0.875rem',
                        }}>
                        Current casting calls and talent searches
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ mb: 6 }}>
                {data.map((role, index) => {
                    const IconComponent = getAuditionIcon(role.audition.audition_type);
                    const iconColor = getAuditionIconColor(role.audition.audition_type);
                    const statusColor = getStatusColor('open');

                    return (
                        <Card
                            key={index}
                            sx={{
                                backgroundColor: theme[themeStyle].pure_black,
                                border: `1px solid ${theme[themeStyle].tag_white_light}`,
                                borderLeft: `4px solid ${iconColor}`,
                                borderRadius: 2,
                                mb: 2,
                            }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        mb: 3,
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                backgroundColor: `${iconColor}20`,
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <IconComponent size={24} color={iconColor} />
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.BOLD_HEADER,
                                                }}>
                                                {role.audition.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                    fontSize: '0.875rem',
                                                }}>
                                                {role.audition.audition_type || 'Audition'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Chip
                                        label={'Open'}
                                        size="small"
                                        sx={{
                                            backgroundColor: `${statusColor}20`,
                                            color: statusColor,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        }}
                                    />
                                </Box>

                                <RichTextViewer
                                    content={
                                        role.audition.synopsis ||
                                        role.audition.description ||
                                        'No description available'
                                    }
                                    textStyle={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        marginBottom: 3,
                                        padding: 0,
                                    }}
                                />

                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6} md={3}>
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                p: 2,
                                                backgroundColor: theme[themeStyle].prisma_dark,
                                                borderRadius: 2,
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.BOLD_HEADER,
                                                    fontWeight: 'semibold',
                                                }}>
                                                {role.audition.min_age && role.audition.max_age
                                                    ? `${role.audition.min_age}-${role.audition.max_age}`
                                                    : role.audition.min_age
                                                    ? `${role.audition.min_age}+`
                                                    : 'Any'}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                    fontSize: '0.875rem',
                                                }}>
                                                Age Range
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                p: 2,
                                                backgroundColor: theme[themeStyle].prisma_dark,
                                                borderRadius: 2,
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.BOLD_HEADER,
                                                    fontWeight: 'semibold',
                                                }}>
                                                {role.audition.shoot_location?.address || 'TBD'}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                    fontSize: '0.875rem',
                                                }}>
                                                Location
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                p: 2,
                                                backgroundColor: theme[themeStyle].prisma_dark,
                                                borderRadius: 2,
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.BOLD_HEADER,
                                                    fontWeight: 'semibold',
                                                    fontSize: '0.875rem',
                                                }}>
                                                {formatCurrencyWithAbbrevation(
                                                    role.audition.max_reward,
                                                    role.audition.currency,
                                                ) || 'TBD'}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                    fontSize: '0.875rem',
                                                }}>
                                                Compensation
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                p: 2,
                                                backgroundColor: theme[themeStyle].prisma_dark,
                                                borderRadius: 2,
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.BOLD_HEADER,
                                                    fontWeight: 'semibold',
                                                }}>
                                                in{' '}
                                                {role.audition.expiration
                                                    ? convertMinuteSecondsDays(role.audition.casting_expiration)
                                                    : 'TBD'}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                    fontSize: '0.875rem',
                                                }}>
                                                Deadline
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                    {role.audition.required_skill?.map((skill, skillIndex) => (
                                        <Chip
                                            key={skillIndex}
                                            label={skill}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                borderColor: theme[themeStyle].tag_white_light,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    ))}
                                    {role.audition.tags?.map((tag, tagIndex) => (
                                        <Chip
                                            key={tagIndex}
                                            label={tag}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                borderColor: theme[themeStyle].tag_white_light,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    ))}
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <CustomButton
                                        label="View Details"
                                        onClick={() => handleViewDetails(role)}
                                        sx={{
                                            width: '100%',
                                            height: '48px',
                                            fontSize: '0.875rem',
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            '&:hover': {
                                                WebkitTextFillColor: theme[themeStyle].primary_orange,
                                            },
                                        }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {/* Pagination */}
            {pagination && (pagination.hasNextPage || pagination.hasPrevPage) && (
                <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={page => {
                        handlePageChange(page);
                    }}
                    hasNextPage={pagination.hasNextPage}
                    hasPreviousPage={pagination.hasPrevPage}
                />
            )}
        </Box>
    );
};

export { AuditionsTab };
